import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useState } from 'react';
import { useRefinementList } from 'react-instantsearch';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Text16 } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  PlayablePosition,
  formatPosition,
  sortByPosition,
} from '@sorare/core/src/lib/players';

import { ButtonLabel, StyledDropdown, StyledDropdownOptionLabel } from '../ui';

const FilterWrapper = styled(Horizontal).attrs({ gap: 1 })`
  overflow-x: auto;
`;
const Filter = styled(ButtonBase)`
  padding: var(--half-unit) var(--double-unit);
  border-radius: var(--unit);
  &.selected {
    background-color: var(--c-neutral-1000);
    color: var(--c-neutral-100);
  }
`;

const FilterLabel = styled(Text16)`
  white-space: nowrap;
`;

export const PositionFilter = () => {
  const { up: isTablet } = useScreenSize('tablet');
  const { formatMessage } = useIntlContext();
  const [selected, setSelected] = useState<string>('ALL');
  const { items, refine: doRefine } = useRefinementList({
    attribute: 'position',
    limit: 40,
  });

  const orderedItems = items
    .sort((a, b) =>
      sortByPosition(b.value as PlayablePosition, a.value as PlayablePosition)
    )
    .reverse();

  const onFilter = (position: string) => {
    if (selected !== 'ALL') doRefine(selected);
    setSelected(position);
    if (position !== 'ALL') doRefine(position);
  };

  const options = [
    {
      value: 'ALL',
      label: formatMessage(glossary.all),
    },
    ...orderedItems.map(({ value, label }) => ({
      value,
      label: formatPosition(label, formatMessage),
    })),
  ];
  if (isTablet)
    return (
      <FilterWrapper>
        {options.map(option => (
          <Filter
            key={option.value}
            className={classNames({ selected: selected === option.value })}
            onClick={() => onFilter(option.value)}
          >
            <FilterLabel bold>{option.label}</FilterLabel>
          </Filter>
        ))}
      </FilterWrapper>
    );
  return (
    <StyledDropdown
      fullWidth={!isTablet}
      closeOnChange
      label={props => (
        <Button {...props} color="tertiary" size="medium" fullWidth={!isTablet}>
          <ButtonLabel gap={1} as="span">
            {options.find(o => o.value === selected)!.label}
            <FontAwesomeIcon icon={faChevronDown} />
          </ButtonLabel>
        </Button>
      )}
      onChange={e => {
        if (e.target) {
          const { value: selectedValue } = e.target as HTMLInputElement;
          if (selectedValue) {
            onFilter(selectedValue);
          }
        }
      }}
    >
      {options.map(option => (
        <StyledDropdownOptionLabel
          key={option.value}
          className={classNames({
            selected: selected === option.value,
          })}
        >
          {option.label}
          <input
            type="radio"
            value={option.value}
            defaultChecked={selected === option.value}
            className="sr-only"
            name="position-filter"
          />
        </StyledDropdownOptionLabel>
      ))}
    </StyledDropdown>
  );
};
